import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';

import type { ButtonAdditionalEffectsSettings } from './type/button-additional-effects-settings.type';
import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
} from '@common/enums/button.enum';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
import { TranslateModule } from '@ngx-translate/core';

const pickEffects = (
    value: Partial<ButtonAdditionalEffectsSettings> | null | undefined
): ButtonAdditionalEffectsSettings => ({
    isActive: value?.isActive,
    effectCondition: value?.effectCondition,
    hoverEffects: value?.hoverEffects,
    staticEffects: value?.staticEffects,
});

@Component({
    selector: 'sp-button-additional-effects-settings',
    templateUrl: './sp-button-additional-effects-settings.component.html',
    styleUrls: ['./sp-button-additional-effects-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpSwitchComponent, TranslateModule],
})
export class SpButtonAdditionalEffectsSettingsComponent {
    public readonly additionalEffects = input<ButtonAdditionalEffectsSettings | null | undefined>(null);
    public readonly valueChangeEvent = output<ButtonAdditionalEffectsSettings>();

    public readonly EEffectCondition = EButtonAdditionalEffectsCondition;
    public readonly EHoverEffects = EButtonAdditionalHoverEffectsTypes;
    public readonly EStaticEffects = EButtonAdditionalStaticEffectsTypes;

    private readonly model = signal<ButtonAdditionalEffectsSettings>(pickEffects(null));

    public readonly isActive = computed(() => Boolean(this.model().isActive));
    public readonly effectCondition = computed(() => this.model().effectCondition);
    public readonly hoverEffects = computed(() => this.model().hoverEffects);
    public readonly staticEffects = computed(() => this.model().staticEffects);

    constructor() {
        effect(() => this.model.set(pickEffects(this.additionalEffects())));
    }

    public setIsActive(isActive: boolean): void {
        this.patch({ isActive });
    }

    public setEffectCondition(event: Event): void {
        this.patch({ effectCondition: this.selected(event) as EButtonAdditionalEffectsCondition });
    }

    public setHoverEffects(event: Event): void {
        this.patch({ hoverEffects: this.selected(event) as EButtonAdditionalHoverEffectsTypes });
    }

    public setStaticEffects(event: Event): void {
        this.patch({ staticEffects: this.selected(event) as EButtonAdditionalStaticEffectsTypes });
    }

    private selected(event: Event): string {
        return (event.target as HTMLSelectElement).value;
    }

    private patch(patch: Partial<ButtonAdditionalEffectsSettings>): void {
        this.model.update((model) => ({ ...model, ...patch }));
        this.valueChangeEvent.emit(this.model());
    }
}
